import { landmineNames } from '@mm/constants/landmine-names';
import AdAccountCRUDActions from '@mm/state/actions/AdAccountCRUDActions';
import AdAudienceCRUDActions from '@mm/state/actions/AdAudienceCRUDActions';
import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import { showSlideoutMenu } from '@mm/state/actions/viewActions';
import {
  openGeneralConfirmModal,
  openNoLinkedAdAccountModal,
  useModalActions,
} from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { selectAudiencesPagination } from '@mm/state/selectors/messengerAdSelectors';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';

export const useAudienceViewHandlers = () => {
  const audiencesCRUDActions = useAudiencesCRUDActions();
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const audiencesPagination = useSelector(selectAudiencesPagination);

  const dispatch: Dispatch<StoreState> = useDispatch();
  const onEdit = (audienceId: number) => {
    dispatch(showSlideoutMenu('editAudience', { audienceId }));
  };

  const modalActions = useModalActions();

  const canCreateCustomAudience = useSelector(
    combinedFeatureFlagsSelector,
  ).audiencesCreateCustom;

  const onDelete = ({
    audienceId,
    audienceName,
    onSuccess,
    options,
  }: {
    audienceId: number;
    audienceName: string;
    onSuccess: () => void;
    options?: {
      sortColumn?: 'created_at' | 'contacts_count';
      sortDirection?: 'asc' | 'desc';
      inUse?: boolean;
    };
  }) => {
    modalActions.openDeleteConfirmModal({
      headerText: `Are you sure you want to delete the audience "${audienceName}"?`,
      deleteCallback: async () => {
        await audiencesCRUDActions.deleteAudience(
          activeChatbotId,
          audienceId,
          1,
          audiencesPagination.perPage,
          options,
        );
        if (onSuccess) onSuccess();
      },
      newStyle: true,
    });
  };

  const facebookPageId = useSelector(activeFacebookPageIdSelector);

  const onSync = async (
    audienceId: number | undefined,
    audienceName?: string,
  ) => {
    if (!audienceId) return;

    const adAccounts = await dispatch(
      AdAccountCRUDActions.retrieveByFbPageId(facebookPageId),
    );

    if (!(adAccounts || []).find(ac => ac.selected)) {
      return dispatch(openNoLinkedAdAccountModal());
    }

    const result = await dispatch(
      AdAudienceCRUDActions.assignAdAudience(facebookPageId, audienceId),
    );

    if (result && result.success) {
      modalActions.openGeneralTextModal({
        header: 'Sync Facebook Custom Audience',
        message: `Audience ${audienceName || 'Audience'} successfully synced`,
      });
    }
  };

  const onClone = async (audienceId: number) => {
    audiencesCRUDActions.cloneAudience(activeChatbotId, audienceId);
  };

  const guardedSyncHandler = (...args: Parameters<typeof onSync>) => {
    if (canCreateCustomAudience) {
      return onSync(...args);
    }
    return modalActions.openUniversalUpgradeLandmineModal({
      landmineName: landmineNames['LEADS__CONTACTS__MAKE-CUSTOM-AUDIENCE'],
      feature: 'audiencesCreateCustom',
    });
  };

  const onAllowContactsDeleteToggle = async (
    audienceId: number,
    shouldDelete: boolean,
  ) => {
    if (!shouldDelete) {
      await audiencesCRUDActions.deleteContacts(
        audienceId,
        activeChatbotId,
        shouldDelete,
      );
      return;
    }

    dispatch(
      openGeneralConfirmModal({
        headerText: 'Warning',
        messageText: `This will continuously be deleting all 
          CONTACTS that meet the criteria for this audience, including contacts that are in other audiences.`,
        secondLineText: `You will not be able to recover these contacts, and they will only be reachable by you if they contact you again`,
        cancelButtonFirst: false,
        okButtonText: 'Enable continuous deletion of contacts',
        cancelButtonText: 'Never mind',
        onCloseHandler: async ok => {
          if (ok) {
            await audiencesCRUDActions.deleteContacts(
              audienceId,
              activeChatbotId,
              shouldDelete,
            );
          }
        },
      }),
    );
  };

  return {
    onEdit,
    onDelete,
    onSync: guardedSyncHandler,
    onAllowContactsDeleteToggle,
    onClone,
  };
};
